/**
 * Avatar Upload Component
 *
 * Allows users to upload and manage their profile avatar
 * Features:
 * - File selection via input or drag-and-drop
 * - Image preview
 * - Upload progress
 * - Remove avatar
 * - File validation (size, type)
 */

import { useState, useRef } from 'react';
import { asApiError } from '@/utils/error.util';
import { useUploadAvatar, useDeleteAvatar } from '@/hooks/useUserSettings';
import { useAuth } from '@/hooks/useAuth';
import Avatar from '../common/Avatar';

interface AvatarUploadProps {
  className?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

export const AvatarUpload = ({ className = '' }: AvatarUploadProps) => {
  const { user } = useAuth();
  const uploadAvatar = useUploadAvatar();
  const deleteAvatar = useDeleteAvatar();

  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  /**
   * Validate file
   */
  const validateFile = (file: File): boolean => {
    setError(null);

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Невалиден тип файл. Моля, качете изображение JPEG, PNG или WebP.');
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      setError('Размерът на файла надвишава лимита от 5MB.');
      return false;
    }

    return true;
  };

  /**
   * Handle file selection
   */
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!validateFile(file)) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadAvatar.mutate(file, {
      onSuccess: () => {
        setPreview(null);
        setError(null);
      },
      onError: (error: unknown) => {
        setError(asApiError(error).response?.data?.message || 'Неуспешно качване на аватар');
        setPreview(null);
      },
    });
  };

  /**
   * Handle drag and drop
   */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!validateFile(file)) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload file
    uploadAvatar.mutate(file, {
      onSuccess: () => {
        setPreview(null);
        setError(null);
      },
      onError: (error: unknown) => {
        setError(asApiError(error).response?.data?.message || 'Неуспешно качване на аватар');
        setPreview(null);
      },
    });
  };

  /**
   * Handle remove avatar
   */
  const handleRemove = () => {
    if (!user.avatar_url) return;

    deleteAvatar.mutate(undefined, {
      onError: (error: unknown) => {
        setError(asApiError(error).response?.data?.message || 'Неуспешно изтриване на аватар');
      },
    });
  };

  /**
   * Open file picker
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const isUploading = uploadAvatar.isPending;
  const isDeleting = deleteAvatar.isPending;

  return (
    <div className={`flex flex-col items-center space-y-4 ${className}`}>
      {/* Avatar Preview */}
      <div className="relative">
        {preview ? (
          <div className="relative w-32 h-32">
            <img
              src={preview}
              alt="Преглед на аватар"
              className="w-full h-full rounded-full object-cover border-4 border-blue-500 shadow-lg"
            />
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">
              Преглед
            </div>
          </div>
        ) : (
          <Avatar user={user} size="lg" />
        )}

        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div
        className={`w-full border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-dark-border-primary hover:border-gray-400 dark:hover:border-dark-text-muted'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />

        <button
          onClick={openFilePicker}
          disabled={isUploading}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
        >
          {isUploading ? 'Качване...' : 'Изберете файл или плъзнете тук'}
        </button>

        <div className="text-xs text-gray-600 dark:text-dark-text-secondary mt-2 space-y-1">
          <p className="font-medium">Изисквания:</p>
          <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-dark-text-muted">
            <li>Формати: PNG, JPG, WebP</li>
            <li>Размер: до 5MB</li>
            <li>Размери: мин. 100×100px, макс. 5000×5000px</li>
            <li>Изход: кръгло изрязване 512×512</li>
          </ul>
          <p className="text-gray-400 dark:text-dark-text-muted italic mt-1">
            💡 Съвет: квадратните изображения изглеждат най-добре като кръгли аватари
          </p>
        </div>
      </div>

      {/* Actions */}
      {user.avatar_url && !isUploading && (
        <button
          onClick={handleRemove}
          disabled={isDeleting}
          className="text-sm text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
        >
          {isDeleting ? 'Премахване...' : 'Премахни аватар'}
        </button>
      )}

      {/* Error Message */}
      {error && (
        <div className="w-full text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default AvatarUpload;
